import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ScenarioCategorySelect } from "@/components/admin/ScenarioCategorySelect";
import { ScenarioMediaUpload } from "@/components/admin/ScenarioMediaUpload";

export default function AdminScenarioUpload() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("behavior_nonviolent");
  const [mediaFile, setMediaFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setTitle("");
    setContent("");
    setMediaFile(null);
    setCategory("behavior_nonviolent");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let media_path = null;
      let media_type = null;
      let format = "text";
      if (mediaFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", mediaFile);
        const { data } = await api.post("/uploads", formData, { headers: { "Content-Type": "multipart/form-data" } });
        media_path = data.storage_path;
        media_type = data.media_type;
        format = "video";
        setUploading(false);
      }
      await api.post("/scenarios", { title, content, category, format, media_path, media_type });
      setSuccess(true);
      resetForm();
      setTimeout(() => setSuccess(false), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setLoading(false);
  };

  const getSubmitLabel = () => {
    if (uploading) return "Uploading...";
    if (loading) return "Publishing...";
    return "Publish to Voting Feed";
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A6E] mb-2">Admin Tool</p>
      <h1 className="font-display font-extrabold uppercase text-2xl md:text-3xl text-zinc-950 tracking-tight mb-2">Add Scenario to the Feed</h1>
      <p className="text-zinc-500 text-sm mb-8">Fast entry for daily content — upload a video/photo, write a caption, submit. No code needed.</p>

      {success && (
        <div data-testid="admin-scenario-upload-success" className="mb-6 p-4 rounded-sm bg-[#4FD1C5]/10 border border-[#4FD1C5]/40 text-[#1E3A6E] font-medium flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5" /> Scenario published to the voting feed.
        </div>
      )}
      {error && <div className="mb-6 p-3 rounded-sm bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 rounded-sm p-6 sm:p-8 space-y-5">
        <ScenarioCategorySelect value={category} onChange={setCategory} />

        <div>
          <Label htmlFor="title" className="text-zinc-500 text-xs uppercase tracking-wide">Title</Label>
          <Input id="title" required value={title} onChange={(e) => setTitle(e.target.value)}
            data-testid="admin-scenario-title-input"
            className="bg-white border-zinc-300 text-zinc-950 focus:border-zinc-950 focus:ring-1 focus:ring-[#4FD1C5] rounded-none mt-1" />
        </div>

        <div>
          <Label htmlFor="content" className="text-zinc-500 text-xs uppercase tracking-wide">Caption / Description</Label>
          <Textarea id="content" required rows={5} value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="Write the caption or scenario description here."
            data-testid="admin-scenario-content-input"
            className="bg-white border-zinc-300 text-zinc-950 focus:border-zinc-950 focus:ring-1 focus:ring-[#4FD1C5] rounded-none mt-1" />
        </div>

        <ScenarioMediaUpload mediaFile={mediaFile} onFileChange={setMediaFile} />

        <Button type="submit" disabled={loading} data-testid="admin-scenario-submit-button" className="w-full bg-zinc-950 text-white font-medium rounded-none hover:bg-zinc-900 hover:shadow-[0px_4px_0px_0px_#4FD1C5]">
          {getSubmitLabel()}
        </Button>
      </form>
    </div>
  );
}
